/**
 * Stato di collegamento del numero WhatsApp attivo (api_configurations).
 * Si aggiorna in tempo reale se la tabella e' nella publication realtime
 * (non risulta dalle migration: l'iscrizione non costa niente se non arriva
 * nulla) e comunque ogni 60 secondi e quando la scheda torna in primo piano.
 */
import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { isNumberDisconnected, numberLabel } from './connectionHealth';

const POLL_MS = 60_000;

export interface NumberHealth {
  disconnected: boolean;
  label: string;
  message: string | null;
}

export function useNumberConnectionHealth(configId: string | null | undefined): NumberHealth | null {
  const [health, setHealth] = useState<NumberHealth | null>(null);

  const apply = useCallback((row: any) => {
    if (!row) { setHealth(null); return; }
    setHealth({
      disconnected: isNumberDisconnected(row.session_health_status),
      label: numberLabel(row),
      message: row.session_health_message || null,
    });
  }, []);

  const load = useCallback(async () => {
    if (!configId) return;
    const { data, error } = await supabase
      .from('api_configurations')
      .select('id, display_name, configuration, session_health_status, session_health_message')
      .eq('id', configId)
      .maybeSingle();
    if (!error) apply(data);
  }, [configId, apply]);

  useEffect(() => {
    setHealth(null);
    if (!configId) return;
    void load();
    const interval = setInterval(() => { void load(); }, POLL_MS);
    const onVisible = () => { if (document.visibilityState === 'visible') void load(); };
    document.addEventListener('visibilitychange', onVisible);
    const channel = supabase
      .channel(`number-health-${configId}`)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'api_configurations', filter: `id=eq.${configId}` },
        (payload) => apply(payload.new))
      .subscribe();
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      supabase.removeChannel(channel);
    };
  }, [configId, load, apply]);

  return health;
}
